/**
 * D4DPanel - 4D Video Analysis Panel
 *
 * Displays 4D representation transformer (D4RT) analysis results:
 * - Point cloud viewer (Three.js)
 * - Depth map overlay
 * - Trajectory tracking
 * - Novel view rendering
 *
 * Integrates with backend D4RT service at /api/d4rt
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Loader2, AlertCircle, Play, Pause, RefreshCw, X, Download, Layers, Film, Navigation } from 'lucide-react';
import PointCloudViewer from './PointCloudViewer';
import DynamicTrajectoryViewer from './DynamicTrajectoryViewer';
import DepthOverlay from './DepthOverlay';
import D4DQueryInterface from './D4DQueryInterface';
import { useD4RTData } from '../../hooks/useD4RTData';

/**
 * D4DPanel Component
 *
 * @param {Object} props
 * @param {string} props.videoId - GCS video URI or ID
 * @param {number} props.currentFrame - Current video frame index
 * @param {boolean} props.isPlaying - Video playback state
 * @param {Object} props.videoDimensions - {width, height}
 * @param {Function} props.onClose - Callback to close panel
 * @param {Object} props.videoRef - Video element ref for click handling
 */
const D4DPanel = ({ videoId, currentFrame, isPlaying, videoDimensions, onClose, videoRef }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [clickMode, setClickMode] = useState(false); // Enable click-to-select mode
  const [depthOverlayVisible, setDepthOverlayVisible] = useState(false); // Toggle depth overlay
  const [colorMode, setColorMode] = useState('depth'); // depth | confidence | rgb
  const [viewMode, setViewMode] = useState('dynamic'); // 'static' | 'dynamic'
  const [interfaceMode, setInterfaceMode] = useState('classic'); // 'classic' | 'navigation'

  // Dynamic view data
  const [skeletalData, setSkeletalData] = useState(null);
  const [pitchData, setPitchData] = useState(null);
  const [isDynamicPlaying, setIsDynamicPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);

  // D4RT service health
  const [serviceAvailable, setServiceAvailable] = useState(null); // null=checking, true/false

  // Use D4RT data hook
  const {
    getDepth,
    trackPoint: trackPointAPI,
    getSkeletalTrack,
    getPitchTrajectory,
    checkHealth,
    isLoading,
    error: hookError,
    clearCache,
  } = useD4RTData();

  const [error, setError] = useState(null);

  // Check D4RT service health on mount (run once)
  useEffect(() => {
    checkHealth().then(health => {
      const isHealthy = health.status === 'healthy';
      setServiceAvailable(isHealthy);
      if (!isHealthy) {
        const hint = health.code === 'D4RT_SERVICE_DOWN'
          ? 'Start D4RT service: cd backend/python/d4rt-service && pip install -r requirements.txt && python app.py'
          : 'D4RT service is not available. Check that the Python service is running on port 8001.';
        setError(hint);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------------
  // useCallback dependency chain (be careful when modifying):
  //
  //   fetchD4DAnalysis  -> [videoId, currentFrame, serviceAvailable, videoDimensions, getDepth]
  //   loadDynamicData   -> [videoId, serviceAvailable, currentFrame, videoDimensions, getSkeletalTrack, getPitchTrajectory]
  //   handleRefresh     -> [clearCache, viewMode, loadDynamicData, fetchD4DAnalysis]
  //   handleVideoClick  -> [videoRef, clickMode, videoId, currentFrame, trackPoint, getDepth]
  //
  // These callbacks are chained: handleRefresh calls loadDynamicData or fetchD4DAnalysis.
  // Changing dependencies in one can cascade stale closures to the others.
  // -----------------------------------------------------------------------

  /**
   * Fetch 4D analysis for current frame
   */
  const fetchD4DAnalysis = useCallback(async () => {
    if (!videoId || currentFrame == null || serviceAvailable !== true) return;

    setError(null);

    try {
      // Get depth at center point
      const result = await getDepth(
        videoId,
        { u: videoDimensions.width / 2, v: videoDimensions.height / 2 },
        currentFrame
      );

      setAnalysisData(result);
    } catch (err) {
      console.error('[D4DPanel] Fetch error:', err);
      setError(err.message);
    }
  }, [videoId, currentFrame, serviceAvailable, videoDimensions, getDepth]);

  /**
   * Track selected point across frames
   */
  const trackPoint = useCallback(async (point) => {
    if (!videoId || !point) return;

    setError(null);

    try {
      const result = await trackPointAPI(
        videoId,
        { u: point.x, v: point.y },
        currentFrame,
        currentFrame + 30, // Track next 30 frames (1 second at 30fps)
        `track_${Date.now()}`
      );

      setTrackingData(result);
    } catch (err) {
      console.error('[D4DPanel] Tracking error:', err);
      setError(err.message);
    }
  }, [videoId, currentFrame, trackPointAPI]);

  /**
   * Load dynamic trajectory data (skeletal + pitch)
   */
  const loadDynamicData = useCallback(async () => {
    if (!videoId || serviceAvailable !== true) return;

    setError(null);

    try {
      // Load skeletal tracking for 60 frames (2 seconds @ 30fps)
      const skeletal = await getSkeletalTrack(
        videoId,
        currentFrame,
        currentFrame + 60
      );
      setSkeletalData(skeletal);

      // Load pitch trajectory (simulate pitch tracking)
      const pitch = await getPitchTrajectory(
        videoId,
        currentFrame,
        currentFrame + 20, // ~0.67 seconds (typical pitch flight time)
        { u: videoDimensions.width / 2, v: videoDimensions.height * 0.4 } // Upper middle (release point)
      );
      setPitchData(pitch);

    } catch (err) {
      console.error('[D4DPanel] Dynamic data load error:', err);
      setError(err.message);
    }
  }, [videoId, serviceAvailable, currentFrame, videoDimensions, getSkeletalTrack, getPitchTrajectory]);

  /**
   * Refresh analysis (clear cache and refetch)
   */
  const handleRefresh = useCallback(() => {
    clearCache();
    if (viewMode === 'dynamic') {
      loadDynamicData();
    } else {
      fetchD4DAnalysis();
    }
  }, [clearCache, viewMode, loadDynamicData, fetchD4DAnalysis]);

  /**
   * Handle video click to select point
   */
  const handleVideoClick = useCallback((event) => {
    if (!videoRef?.current || !clickMode) return;

    const video = videoRef.current;
    const rect = video.getBoundingClientRect();

    // Get click coordinates relative to video element
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // Normalize to video dimensions (0-width, 0-height)
    const u = (x / rect.width) * video.videoWidth;
    const v = (y / rect.height) * video.videoHeight;

    const point = { x: u, y: v };
    setSelectedPoint(point);

    // Start tracking from this point
    trackPoint(point);

    // Get depth at this point
    getDepth(videoId, { u, v }, currentFrame)
      .then(result => {
        setAnalysisData(result);
      })
      .catch(err => {
        console.error('[D4DPanel] Depth analysis error:', err);
      });

  }, [videoRef, clickMode, videoId, currentFrame, trackPoint, getDepth]);

  /**
   * Toggle click mode
   */
  const toggleClickMode = () => {
    setClickMode(!clickMode);
  };

  /**
   * Export 3D data as JSON
   */
  const exportData = useCallback(() => {
    const exportObj = {
      video_id: videoId,
      frame: currentFrame,
      selected_point: selectedPoint,
      depth_analysis: analysisData,
      tracking: trackingData,
      exported_at: new Date().toISOString(),
    };

    const dataStr = JSON.stringify(exportObj, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `d4rt_${videoId}_frame${currentFrame}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [videoId, currentFrame, selectedPoint, analysisData, trackingData]);

  /**
   * Keyboard shortcuts
   */
  useEffect(() => {
    const handleKeyPress = (e) => {
      // ESC = close panel
      if (e.key === 'Escape') {
        e.preventDefault();
        if (onClose) onClose();
        return;
      }

      // D = toggle 4D panel (handled by parent)
      // R = refresh
      if (e.key === 'r' || e.key === 'R') {
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          handleRefresh();
        }
      }
      // E = export
      if (e.key === 'e' || e.key === 'E') {
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          exportData();
        }
      }
      // L = toggle depth overlay
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        setDepthOverlayVisible(!depthOverlayVisible);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleRefresh, exportData, depthOverlayVisible, onClose]);

  /**
   * Color mode cycling
   */
  const cycleColorMode = () => {
    const modes = ['depth', 'confidence', 'rgb'];
    const currentIndex = modes.indexOf(colorMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setColorMode(nextMode);
  };

  // Load dynamic data when switching to dynamic view
  useEffect(() => {
    if (viewMode === 'dynamic' && !skeletalData && serviceAvailable === true) {
      loadDynamicData();
    }
  }, [viewMode, skeletalData, loadDynamicData, serviceAvailable]);

  // Fetch analysis when frame changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isPlaying && !selectedPoint && viewMode === 'static') {
        // Only fetch when paused and no point selected to avoid spamming
        fetchD4DAnalysis();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [currentFrame, videoId, isPlaying, selectedPoint, viewMode, fetchD4DAnalysis]);

  // Add click listener to video when click mode is enabled
  useEffect(() => {
    const video = videoRef?.current;
    if (!video || !clickMode) return;

    video.addEventListener('click', handleVideoClick);
    video.style.cursor = 'crosshair';

    return () => {
      video.removeEventListener('click', handleVideoClick);
      video.style.cursor = 'default';
    };
  }, [videoRef, clickMode, handleVideoClick]);

  // If navigation mode, render D4DQueryInterface instead
  if (interfaceMode === 'navigation') {
    return <D4DQueryInterface videoId={videoId} onClose={() => setInterfaceMode('classic')} />;
  }

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50">
      <div className="bg-zinc-900 border border-cyan-500/30 rounded-lg p-6 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 border-b border-cyan-500/20 pb-4">
          <div className="flex items-center gap-3">
            <Box className="text-cyan-400" size={24} />
            <div>
              <h2 className="text-xl font-bold text-white">4D Video Analysis</h2>
              <p className="text-sm text-zinc-400">Real-time Biomechanics Tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mode toggle button */}
            <button
              onClick={() => setInterfaceMode('navigation')}
              className="px-3 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 transition-colors text-sm font-medium flex items-center gap-2"
              title="Switch to 4D Navigation Interface"
            >
              <Navigation size={18} />
              4D Navigation
            </button>
            <button
              onClick={() => setViewMode(viewMode === 'dynamic' ? 'static' : 'dynamic')}
              className={`px-3 py-1.5 rounded-lg transition-colors text-sm font-medium flex items-center gap-2 ${
                viewMode === 'dynamic'
                  ? 'bg-purple-500 text-white'
                  : 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400'
              }`}
              title={`View: ${viewMode === 'dynamic' ? 'Dynamic Motion' : 'Static Point Cloud'}`}
            >
              <Film size={16} />
              {viewMode === 'dynamic' ? 'Dynamic' : 'Static'}
            </button>
            {viewMode === 'static' && (
              <button
                onClick={toggleClickMode}
                className={`px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${
                  clickMode
                    ? 'bg-cyan-500 text-white'
                    : 'bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400'
                }`}
                title={clickMode ? 'Click mode ON - Click video to select point' : 'Click mode OFF'}
              >
                {clickMode ? '🎯 Click Mode ON' : 'Enable Click Mode'}
              </button>
            )}
            <button
              onClick={() => setDepthOverlayVisible(!depthOverlayVisible)}
              className={`p-2 rounded-lg transition-colors ${
                depthOverlayVisible
                  ? 'bg-cyan-500 text-white'
                  : 'bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400'
              }`}
              title={`Depth overlay: ${depthOverlayVisible ? 'ON' : 'OFF'} (L)`}
            >
              <Layers size={18} />
            </button>
            <button
              onClick={cycleColorMode}
              className="px-2 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors text-xs font-mono text-cyan-400"
              title="Color mode (click to cycle)"
            >
              {colorMode.toUpperCase()}
            </button>
            <button
              onClick={exportData}
              className="p-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors"
              title="Export 3D data (E)"
            >
              <Download className="text-cyan-400" size={18} />
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 transition-colors disabled:opacity-50"
              title="Refresh analysis (R)"
            >
              <RefreshCw className={`text-cyan-400 ${isLoading ? 'animate-spin' : ''}`} size={18} />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-colors border border-red-500/50"
                title="Close 4D panel (ESC)"
              >
                <X className="text-red-300" size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Service Unavailable Banner */}
        {serviceAvailable === false && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="text-amber-400" size={20} />
              <div>
                <p className="text-amber-300 font-medium">GPU Analysis Service Offline</p>
                <p className="text-amber-200/70 text-sm">
                  4D analysis requires a GPU service. Gaze tracking and standard overlays are still available.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-cyan-400" size={32} />
            <span className="ml-3 text-zinc-300">Analyzing frame {currentFrame}...</span>
          </div>
        )}

        {/* Error State - only show if service is available (not a connectivity issue) */}
        {(error || hookError) && !isLoading && serviceAvailable !== false && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-red-300 font-medium">Analysis Failed</p>
                  <p className="text-red-200/70 text-sm mt-1">{error || hookError}</p>
                </div>
              </div>
              <button
                onClick={handleRefresh}
                className="px-3 py-1 text-sm bg-red-500/20 hover:bg-red-500/30 rounded transition-colors text-red-200"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {analysisData && !isLoading && (
          <div className="space-y-4">
            {/* Selected Point Info */}
            {selectedPoint && (
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-cyan-400 font-medium">Selected Point</p>
                    <p className="text-sm text-white">
                      ({selectedPoint.x.toFixed(0)}, {selectedPoint.y.toFixed(0)})
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedPoint(null);
                      setTrackingData(null);
                    }}
                    className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Depth Information */}
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-cyan-500/20">
              <h3 className="text-sm font-semibold text-cyan-400 mb-3">
                Depth Estimation {selectedPoint ? '(at selected point)' : '(center point)'}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-zinc-400">Depth (meters)</p>
                  <p className="text-2xl font-bold text-white">
                    {analysisData.depth_m ? analysisData.depth_m.toFixed(2) : 'N/A'}m
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Confidence</p>
                  <p className="text-2xl font-bold text-cyan-400">
                    {analysisData.confidence ? (analysisData.confidence * 100).toFixed(0) : 'N/A'}%
                  </p>
                </div>
              </div>
            </div>

            {/* 3D Viewer (Dynamic or Static) */}
            <div className="bg-zinc-800/50 rounded-lg p-4 border border-cyan-500/20 relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-cyan-400">
                  {viewMode === 'dynamic' ? '4D Motion Analysis' : '3D Biomechanics Skeleton'}
                </h3>
                <div className="flex items-center gap-2">
                  {viewMode === 'dynamic' && (
                    <>
                      <button
                        onClick={() => setIsDynamicPlaying(!isDynamicPlaying)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          isDynamicPlaying
                            ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                            : 'bg-green-500/20 text-green-300 border border-green-500/50'
                        }`}
                      >
                        {isDynamicPlaying ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <select
                        value={playbackSpeed}
                        onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                        className="text-xs bg-zinc-700 text-zinc-300 rounded px-2 py-1 border border-zinc-600"
                      >
                        <option value="0.25">0.25x</option>
                        <option value="0.5">0.5x</option>
                        <option value="1.0">1x</option>
                        <option value="2.0">2x</option>
                      </select>
                    </>
                  )}
                  <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded border border-green-500/30">
                    {viewMode === 'dynamic' ? 'Motion Tracking' : 'Live Tracking'}
                  </span>
                </div>
              </div>
              <p className="text-xs text-zinc-500 mb-3">
                {viewMode === 'dynamic'
                  ? 'Showing time-based skeletal motion and pitch trajectory with 4D representation. Blue=past, Cyan=present, Green=future.'
                  : 'Showing real-time body joint positions and confidence scores. This is actual biomechanics data from the video analysis.'}
              </p>
              <div className="h-[500px] relative">
                {viewMode === 'dynamic' ? (
                  <DynamicTrajectoryViewer
                    skeletalData={skeletalData}
                    pitchData={pitchData}
                    isPlaying={isDynamicPlaying}
                    playbackSpeed={playbackSpeed}
                    onFrameChange={(_frameIdx, _frameData) => {
                      // Optional: update UI with current frame info
                    }}
                  />
                ) : (
                  <PointCloudViewer
                    points={[]} // Will be populated from D4RT service
                    trajectory={trackingData?.trajectory || []}
                    colorMode={colorMode}
                    showGrid={true}
                    autoRotate={false}
                  />
                )}
                {/* Depth overlay on video (positioned absolutely if video ref available) */}
                {depthOverlayVisible && analysisData?.depth_map && viewMode === 'static' && (
                  <div className="absolute top-0 left-0 pointer-events-none">
                    <DepthOverlay
                      depthData={analysisData.depth_map}
                      width={videoDimensions.width}
                      height={videoDimensions.height}
                      opacity={0.4}
                      visible={depthOverlayVisible}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Instructions & Keyboard Shortcuts */}
            <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-zinc-300 mb-2">Instructions</h3>
                  <ul className="space-y-2 text-sm text-zinc-400">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-400 mt-0.5">•</span>
                      <span>
                        {clickMode ? (
                          <strong className="text-cyan-300">Click on video</strong>
                        ) : (
                          'Enable click mode, then click video'
                        )} to select a point for tracking
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-400 mt-0.5">•</span>
                      <span>Pause video to see detailed depth analysis</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-400 mt-0.5">•</span>
                      <span>3D viewer shows trajectory with confidence colors</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-zinc-300 mb-2">Keyboard Shortcuts</h3>
                  <ul className="space-y-1 text-xs text-zinc-400 font-mono">
                    <li><span className="text-red-400">ESC</span> - Close this panel</li>
                    <li><span className="text-cyan-400">R</span> - Refresh analysis</li>
                    <li><span className="text-cyan-400">E</span> - Export 3D data (JSON)</li>
                    <li><span className="text-cyan-400">L</span> - Toggle depth overlay</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Video Info */}
            <div className="text-xs text-zinc-500">
              <p>Video ID: {videoId || 'N/A'}</p>
              <p>Frame: {currentFrame} / Dimensions: {videoDimensions.width}×{videoDimensions.height}</p>
            </div>
          </div>
        )}

        {/* Placeholder State (No Analysis Yet) */}
        {!analysisData && !isLoading && !error && (
          <div className="text-center py-12">
            <Box className="mx-auto text-cyan-400/50 mb-4" size={48} />
            <p className="text-zinc-400">Pause the video to analyze a frame</p>
            <p className="text-zinc-500 text-sm mt-2">4D depth estimation and point tracking</p>
          </div>
        )}

        {/* Tracking Data (if available) */}
        {trackingData && (
          <div className="mt-4 bg-zinc-800/50 rounded-lg p-4 border border-cyan-500/20">
            <h3 className="text-sm font-semibold text-cyan-400 mb-2">Point Tracking</h3>
            <p className="text-xs text-zinc-400">
              Tracked {trackingData.trajectory?.length || 0} frames
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              Confidence: {trackingData.confidence?.[0]?.toFixed(2) || 'N/A'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default D4DPanel;
